import { useMemo } from 'react';

import { useStoreContext } from 'features/store';

import usePosVendor from 'hooks/menu/use-pos-vendor';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyUnavailableLogger } from 'state/loyalty/hooks/use-loyalty-evaluation-result-logger';
import { getWalmartSeasonalConfigOfferIds } from 'state/loyalty/hooks/utils/walmart';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';

import { useLoyaltyWalmart } from '../loyalty-partners/walmart/use-loyalty-walmart';

import { useGetCmsAndLoyaltyOffers } from './use-get-cms-and-loyalty-offers';
import { useReduceOffers } from './use-offers-reducer';
import { isLegacyMenuOfferEntityAvailable, isOneMenuOfferEntityAvailable } from './utils';

export const useLoyaltyBaseOffers = () => {
  // Loyalty offers
  const {
    cmsOffers,
    cmsOfferGroups,
    personalizedOffers,
    baseOffers,
    offersLoading,
    refreshOffers,
    refreshOffersLoading,
  } = useGetCmsAndLoyaltyOffers();

  const { offers, offersInCart, offerGroups, reductionLoading, reprocessOffers } = useReduceOffers({
    cmsOffers,
    cmsOfferGroups,
    personalizedOffers,
    baseOffers,
    offersLoading,
    refreshOffersLoading,
  });

  // Menu Availability
  const { prices } = useStoreContext();
  const { vendor } = usePosVendor();
  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const { indexedMenu } = useUnifiedMenuContext();
  const { posServiceMode } = useMenuQueryOptions();
  const unavailableLogger = useLoyaltyUnavailableLogger('useLoyaltyBaseOffers');

  // Walmart
  const { data: loyaltyWalmart, loading: isLoyaltyWalmartLoading } = useLoyaltyWalmart();
  const showWalmartSeasonalPromo = useFlag(LaunchDarklyFlag.SHOW_WALMART_SEASONAL_PROMO);

  // Return values
  const loading =
    offersLoading || reductionLoading || refreshOffersLoading || isLoyaltyWalmartLoading;

  const filteredOffers = useMemo(() => {
    if (loading) {
      return offers;
    }

    const walmartSeasonalOffers = getWalmartSeasonalConfigOfferIds(loyaltyWalmart);

    return offers.filter(offer => {
      // Discard seasonal promo if flag is OFF
      if (!showWalmartSeasonalPromo && walmartSeasonalOffers.includes(offer._id)) {
        return false;
      }
      const isAvailable = isUnifiedMenuEnabled
        ? isOneMenuOfferEntityAvailable(offer, indexedMenu, posServiceMode)
        : isLegacyMenuOfferEntityAvailable(offer, prices, vendor);

      // Discard unavailable offer
      if (!isAvailable) {
        unavailableLogger({ loyaltyItem: offer, loyaltyItemType: 'Offer' });
        return false;
      }

      return true;
    });
  }, [
    loading,
    loyaltyWalmart,
    offers,
    showWalmartSeasonalPromo,
    isUnifiedMenuEnabled,
    indexedMenu,
    posServiceMode,
    prices,
    vendor,
    unavailableLogger,
  ]);

  return {
    offers: filteredOffers,
    offersInCart,
    offerGroups,
    reprocessOffers,
    refreshOffers,
    loading,
    baseOffersLoading: offersLoading,
  };
};
